import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SeoHead from '@components/SeoHead';
import GameCard from '@components/GameCard';
import {
  getCategoryBySlug,
  getGamesByCategory,
  getFeaturedGames,
  getTrendingGames,
  getLatestGames,
} from '@lib/content';
import {
  buildCategorySchema,
  buildCategoryBreadcrumbSchema,
  combineSchemas,
} from '@lib/jsonld';
import { getPrimaryCategories } from '@lib/game-utils';

const ITEMS_PER_LOAD = 12;
const CategoryPage: React.FC = () => {
  const { slug } = useParams();

  const category = slug ? getCategoryBySlug(slug) : null;

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_LOAD);

  const allGames = useMemo(() => {
    if (!slug) return [];

    if (slug === 'featured') {
      return getFeaturedGames();
    } else if (slug === 'trending') {
      return getTrendingGames();
    } else if (slug === 'latest') {
      return getLatestGames();
    } else {
      return getGamesByCategory(slug);
    }
  }, [slug]);

  const games = useMemo(() => {
    // Categories that support load more functionality
    const loadMoreCategories = [
      'latest',
      'featured',
      'trending',
      'shooter',
      'horror',
      'parkour-runner',
      'racing',
      'sports',
      'puzzle',
      'adventure',
      'multiplayer',
      'funny',
      'simulator',
      'stickman',
      'franchise'
    ];

    if (slug && loadMoreCategories.includes(slug)) {
      return allGames.slice(0, visibleCount);
    }
    return allGames;
  }, [slug, allGames, visibleCount]);

  const primaryCategories = useMemo(() => getPrimaryCategories(), []);

  const jsonLdSchemas = useMemo(() => {
    if (!category || !games.length) return undefined;

    const categorySchema = buildCategorySchema(category, games);
    const breadcrumbSchema = buildCategoryBreadcrumbSchema(category);

    return combineSchemas(categorySchema, breadcrumbSchema);
  }, [category, games]);

  if (!slug || !category) {
    return <p className="muted">Category not found.</p>;
  }

  return (
    <>
      <SeoHead
        title={`${category.name} games`}
        description={category.description ?? `Play ${category.name} games online.`}
        url={`/category/${slug}`}
        jsonLd={jsonLdSchemas}
      />

      {/* ===== HEADER CATEGORY ===== */}
      <section className="masthead">
        <div className="container">
          <div className="row mb-2">
            <div className="text-center mx-auto">
              <h1 className="display-6 fw-bold">
                {category.name.toUpperCase()} GAMES
              </h1>
              <h2>{category.description}</h2>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BODY ===== */}
      <div className="page-body">
        <div className="container">

          {/* ===== CATEGORY TABS ===== */}
          {/* ===== CATEGORY TABS ===== */}
          <div className="category-pills">
            {[
              'shooter',
              'horror',
              'parkour-runner',
              'racing',
              'sports',
              'puzzle',
              'adventure',
              'multiplayer',
              'funny',
              'simulator',
              'stickman',
              'franchise'
            ].map((slugItem) => (
              <Link
                key={slugItem}
                className={`category-pill ${slugItem === category.slug ? 'active' : ''}`}
                to={`/category/${slugItem}`}
              >
                {slugItem.toUpperCase()}
              </Link>
            ))}
          </div>


          {/* ===== GAME LIST ===== */}
          <section className="my-5 game-section" id="Featured">
            <h2 className="text-center section-title">
              {`${category.name.toUpperCase()} GAMES (${allGames.length})`}
            </h2>

            <div className="row">
              {games.map((game) => (
                <div
                  key={game.slug}
                  className="col-6 col-sm-6 col-md-4 col-lg-3 col-xl-2 game-animate"
                >
                  <GameCard game={game} />
                </div>
              ))}
            </div>

            {/* LOAD MORE cho latest, racing, arcade, action, adventure, puzzle, strategy */}
            {slug && [
              'latest',
              'featured',
              'trending',
              'shooter',
              'horror',
              'parkour-runner',
              'racing',
              'sports',
              'puzzle',
              'adventure',
              'multiplayer',
              'funny',
              'simulator',
              'stickman',
              'franchise'
            ].includes(slug) && visibleCount < allGames.length && (
                <div className="text-center mt-4">
                  <button
                    className="btn btn-outline-primary px-4 py-2 load-more-btn"
                    onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_LOAD)}
                  >
                    Load More
                  </button>
                </div>
              )}
          </section>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;
